import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { isContentTypeEnabledInCurrentEnv } from '@atlassian/content-types-rollout';
import { type EntityATI } from '@atlassian/search-client/common/types';

import type { CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import type { FetchedSelectFilterOption } from '../../../../../common/constants/filters/select-filter/types';
import {
	allTypeFilterValueKeys,
	TypeFilterValueKey,
	type TypeFilterValueKey as TypeFilterValueKeyType,
} from '../../../../../common/constants/filters/universal-type';
import {
	is3pProductKey,
	ProductKeys as ProductKey,
	type ProductKeys,
	type ProductKeys3P,
} from '../../../../../common/constants/product-keys';
import { type ThirdPartyConfigsBootstrap } from '../../../../../common/constants/schemas/3p-config';
import { ContentType } from '../../../../../common/constants/schemas/query-params';
import { isImpersonationEnabled } from '../../../../../common/utils/filters';
import {
	CONFLUENCE_COMMENTS_AND_ATTACHMENTS_ENTITIES,
	getTypeMapping,
} from '../../../../../common/utils/filters/constants';

import { getTypeOptionMessageKey } from './get-type-option-message-key';
import { isEntityEnabled } from './is-entity-enabled';
import { isProductEntitiesIncluded } from './is-product-entities-included';
import { lookupMatches } from './lookup-matches';
import { typeLabels } from './messages';

// eslint-disable-next-line no-barrel-files/no-barrel-files
export { isProductEntitiesIncluded } from './is-product-entities-included';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { isEntityEnabled } from './is-entity-enabled';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { lookupMatches } from './lookup-matches';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { getTypeOptionMessageKey } from './get-type-option-message-key';

type FilterOption = FetchedSelectFilterOption & { products: ProductKeys[] };

/** Slides type filter + slide ATI require env rollout and cc-mui-slides-experiment (same as Quick Find supported entities). */
function isSlideTypeFilterEnabled(): boolean {
	return (
		isContentTypeEnabledInCurrentEnv('slide') &&
		FeatureGates.getExperimentValue<boolean>('cc-mui-slides-experiment', 'isEnabled', false, {
			fireExperimentExposure: true,
		}) === true
	);
}

/** Type filter keys that may be shown and used for entity expansion in the current environment (e.g. slides gated by Confluence rollout). */
export const getAllTypeFilterKeysForSearch: () => TypeFilterValueKeyType[] = () =>
	allTypeFilterValueKeys.filter(
		(key) => key !== TypeFilterValueKey.Slide || isSlideTypeFilterEnabled(),
	);

/**
 * Given the user's search input within the Type filter,
 * return the matching types to the user.
 */
export const lookupOptions: (query: string, config: CloudConfig) => FetchedSelectFilterOption[] = (
	query: string,
	config: CloudConfig,
): FetchedSelectFilterOption[] => {
	const allOptions = getAllTypeOptions(config);
	const matchingOptions = allOptions
		.filter((option) => lookupMatches(query, option.label))
		.map((option) => ({
			label: option.label,
			trackingKey: option.value,
			value: option.value,
			queryParamValue: option.queryParamValue,
			isDefault: option.isDefault,
			product: config.product,
		}));
	return matchingOptions;
};

const combineEntitiesForResultsCountQuery = (
	selectedEntities: Set<EntityATI>,
	unselectedEntities: Set<EntityATI>,
) => {
	const entitiesToExclude: EntityATI[] = [
		'ati:cloud:trello:card',
		...CONFLUENCE_COMMENTS_AND_ATTACHMENTS_ENTITIES,
	];

	return Array.from([
		...new Set<EntityATI>([...selectedEntities, ...unselectedEntities]),
		// We always exclude confluence comments and attachments in the ResultsCountQuery in order to ensure the
		// counts are consistent. More information on this decision here: https://hello.atlassian.net/wiki/x/XC3NRgE
	])
		.filter((entity) => !entitiesToExclude.includes(entity))
		.sort();
};

/**
 * This function is used for populating the entities for the search request
 */
export const getEntitiesForQuery: (
	products: ProductKeys[],
	selectedTypes: TypeFilterValueKeyType[],
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	availableProducts: ProductKeys[],
	selectedProducts: ProductKeys[] | undefined,
) => {
	entitiesForQuery: EntityATI[];
	entitiesForResultsCountQuery: EntityATI[];
} = (
	products: ProductKeys[],
	selectedTypes: TypeFilterValueKeyType[],
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	availableProducts: ProductKeys[],
	selectedProducts: ProductKeys[] | undefined,
): {
	entitiesForQuery: EntityATI[];
	entitiesForResultsCountQuery: EntityATI[];
} => {
	const selectedTypesFiltered = selectedTypes.filter(
		(t) => t !== TypeFilterValueKey.Slide || isSlideTypeFilterEnabled(),
	);

	if (fg('confluence_performance_improve_search_query')) {
		return getEntitiesForQueryPerformanceImproved({
			products,
			selectedTypes: selectedTypesFiltered,
			thirdPartyConfigs,
			availableProducts,
			selectedProducts,
		});
	}

	const selectedEntities = new Set<EntityATI>();
	const unselectedEntities = new Set<EntityATI>();
	// Expand types to include all when:
	// 1. No types are selected (show all results), OR
	// 2. Bug fix is enabled AND specific noun product+type combos are selected:
	//    - Atlas + (project OR goal)
	//    - WebCrawler + web-page
	// because despite type being selected (which would usually mean only showing result counts for that type),
	// we want to show all result counts on the right panel to make nouns more consistent with other apps.
	const shouldIncludeAllTypes =
		selectedTypesFiltered.length === 0 ||
		(((selectedTypesFiltered.includes(TypeFilterValueKey.Project) &&
			selectedProducts?.includes(ProductKey.Atlas)) ||
			(selectedTypesFiltered.includes(TypeFilterValueKey.Goal) &&
				selectedProducts?.includes(ProductKey.Atlas)) ||
			(selectedTypesFiltered.includes(TypeFilterValueKey.WebPage) &&
				selectedProducts?.includes(ProductKey.AtlassianWebCrawler))) &&
			fg('disappearing_result_counts_bug_fix'));
	const types = shouldIncludeAllTypes ? getAllTypeFilterKeysForSearch() : selectedTypesFiltered;

	const typeMapping = getTypeMapping();

	const otherProducts = availableProducts.filter((product) => !products.includes(product));
	types.forEach((type) => {
		const productToEntityMap = typeMapping[type].entities;
		products.forEach((product) => {
			if (isProductEntitiesIncluded(products, product, thirdPartyConfigs)) {
				productToEntityMap[product]?.forEach((ati) => {
					// Logic specific to Atlas product to ensure projects and goals are not shown together
					// in the search query results since they both belong to the same product, Townsquare
					if (fg('disappearing_result_counts_bug_fix')) {
						if (
							product === ProductKey.Atlas &&
							selectedTypesFiltered.length > 0 &&
							!selectedTypesFiltered.includes(type)
						) {
							// Add other entities under the same product to unselected so they appear in right panel counts
							unselectedEntities.add(ati);
							return;
						}
					}

					if (isEntityEnabled(ati, selectedTypesFiltered, selectedProducts)) {
						selectedEntities.add(ati);
					}
				});
			}
		});
		// collect unselected entities
		otherProducts.forEach((product) => {
			if (isProductEntitiesIncluded(otherProducts, product, thirdPartyConfigs)) {
				productToEntityMap[product]?.forEach((ati) => {
					if (!selectedEntities.has(ati)) {
						unselectedEntities.add(ati);
					}
				});
			}
		});
	});

	/// result count wants all entities for selected type
	const entitiesForResultsCountQuery = combineEntitiesForResultsCountQuery(
		selectedEntities,
		unselectedEntities,
	);

	// Do not show Atlas goal and tag results when no product is selected
	if (types.includes('project') && products.length > 1) {
		selectedEntities.delete('ati:cloud:townsquare:goal');
		selectedEntities.delete('ati:cloud:townsquare:tag');
	}

	if (types.includes('customer-contact') && !fg('afm_customer_contact_search_type')) {
		selectedEntities.delete('ati:cloud:graph:customer-contact');
	}

	if (!isSlideTypeFilterEnabled()) {
		selectedEntities.delete('ati:cloud:confluence:slide');
	}

	// Sorting helps a ton in testing
	const entitiesForQuery = Array.from(selectedEntities).sort();

	return {
		entitiesForQuery,
		entitiesForResultsCountQuery,
	};
};

const isProductEntitiesIncludedSimplified = (
	availableProducts: ProductKeys[],
	product: ProductKeys,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
) => {
	// Main change from the original code
	if (is3pProductKey(product)) {
		return (
			availableProducts.includes(product) &&
			(!thirdPartyConfigs[product as ProductKeys3P]?.userNeedsOAuth ||
				isImpersonationEnabled(product, thirdPartyConfigs))
		);
	}

	return availableProducts.includes(product);
};
const getProductsWithEntities = (
	products: ProductKeys[],
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
): ProductKeys[] => {
	return products.reduce<ProductKeys[]>((acc, product) => {
		if (isProductEntitiesIncludedSimplified(products, product, thirdPartyConfigs)) {
			acc.push(product);
		}

		return acc;
	}, []);
};

export const getEntitiesForQueryPerformanceImproved: ({
	products,
	selectedTypes,
	thirdPartyConfigs,
	availableProducts,
	selectedProducts,
}: {
	products: ProductKeys[];
	selectedTypes: TypeFilterValueKeyType[];
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	availableProducts: ProductKeys[];
	selectedProducts: ProductKeys[] | undefined;
}) => {
	entitiesForQuery: EntityATI[];
	entitiesForResultsCountQuery: EntityATI[];
} = ({
	products,
	selectedTypes,
	thirdPartyConfigs,
	availableProducts,
	selectedProducts,
}: {
	products: ProductKeys[];
	selectedTypes: TypeFilterValueKeyType[];
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	availableProducts: ProductKeys[];
	selectedProducts: ProductKeys[] | undefined;
}): {
	entitiesForQuery: EntityATI[];
	entitiesForResultsCountQuery: EntityATI[];
} => {
	const selectedTypesFiltered = selectedTypes.filter(
		(t) => t !== TypeFilterValueKey.Slide || isSlideTypeFilterEnabled(),
	);

	const otherProducts = availableProducts.filter((product) => !products.includes(product));
	const productsWithEntities = getProductsWithEntities(products, thirdPartyConfigs);

	const otherProductsWithEntities = otherProducts.reduce<ProductKeys[]>((acc, otherProduct) => {
		if (isProductEntitiesIncluded(otherProducts, otherProduct, thirdPartyConfigs)) {
			acc.push(otherProduct);
		}

		return acc;
	}, []);

	// Stuff that we can't be cached that easy
	const selectedEntities = new Set<EntityATI>();
	const unselectedEntities = new Set<EntityATI>();
	// Expand types to include all when:
	// 1. No types are selected (show all results), OR
	// 2. Bug fix is enabled AND specific noun product+type combos are selected:
	//    - Atlas + (project OR goal)
	//    - WebCrawler + web-page
	// because despite type being selected (which would usually mean only showing result counts for that type),
	// we want to show all result counts on the right panel to make nouns more consistent with other apps.
	const shouldIncludeAllTypes =
		selectedTypesFiltered.length === 0 ||
		(((selectedTypesFiltered.includes(TypeFilterValueKey.Project) &&
			selectedProducts?.includes(ProductKey.Atlas)) ||
			(selectedTypesFiltered.includes(TypeFilterValueKey.Goal) &&
				selectedProducts?.includes(ProductKey.Atlas)) ||
			(selectedTypesFiltered.includes(TypeFilterValueKey.WebPage) &&
				selectedProducts?.includes(ProductKey.AtlassianWebCrawler))) &&
			fg('disappearing_result_counts_bug_fix'));

	const types = shouldIncludeAllTypes ? getAllTypeFilterKeysForSearch() : selectedTypesFiltered;
	const typeMapping = getTypeMapping();

	// Using normal-loop to reduce overhead and improve performance
	for (let i = 0; i < types.length; i++) {
		const type = types[i];

		const productToEntityMap = typeMapping[type].entities;
		for (let h = 0; h < productsWithEntities.length; h++) {
			const product = productsWithEntities[h];
			const arr = productToEntityMap[product];

			if (!arr || !Array.isArray(arr)) {
				continue;
			}

			for (let w = 0; w < arr.length; w++) {
				const ati = arr[w];

				// Logic specific to Atlas product to ensure projects and goals are not shown together
				// in the search query results since they both belong to the same product, Townsquare
				if (fg('disappearing_result_counts_bug_fix')) {
					if (
						product === ProductKey.Atlas &&
						selectedTypesFiltered.length > 0 &&
						!selectedTypesFiltered.includes(type)
					) {
						// Add other entities under the same product to unselected so they appear in right panel counts
						unselectedEntities.add(ati);
						continue;
					}
				}

				if (isEntityEnabled(ati, selectedTypesFiltered, selectedProducts)) {
					selectedEntities.add(ati);
				}
			}
		}

		for (let j = 0; j < otherProductsWithEntities.length; j++) {
			const product = otherProductsWithEntities[j];

			const arr = productToEntityMap[product];

			if (!arr || !Array.isArray(arr)) {
				continue;
			}

			for (let w = 0; w < arr.length; w++) {
				const ati = arr[w];
				if (!selectedEntities.has(ati)) {
					unselectedEntities.add(ati);
				}
			}
		}
	}

	/// result count wants all entities for selected type
	const entitiesForResultsCountQuery = combineEntitiesForResultsCountQuery(
		selectedEntities,
		unselectedEntities,
	);

	// Do not show Atlas goal and tag results when no product is selected
	if (types.includes('project') && products.length > 1) {
		selectedEntities.delete('ati:cloud:townsquare:goal');
		selectedEntities.delete('ati:cloud:townsquare:tag');
	}

	if (!isSlideTypeFilterEnabled()) {
		selectedEntities.delete('ati:cloud:confluence:slide');
	}

	// Sorting helps a ton in testing
	const entitiesForQuery = Array.from(selectedEntities).sort();

	return {
		entitiesForQuery,
		entitiesForResultsCountQuery,
	};
};

export const getAllTypeOptions: (config: CloudConfig) => FilterOption[] = (
	config: CloudConfig,
): FilterOption[] => {
	const typeMapping = getTypeMapping();

	const allOption: FilterOption = {
		products: [],
		label: config.intl?.formatMessage(typeLabels['all']) || '',
		value: 'all',
		trackingKey: 'all',
		queryParamValue: ContentType['All'],
		isDefault: true,
		product: config.product,
	};

	const typeOptions: FilterOption[] = getAllTypeFilterKeysForSearch()
		.filter(
			(type) =>
				type !== 'customer-contact' ||
				(type === 'customer-contact' && fg('afm_customer_contact_search_type')),
		)
		.map((type) => {
			let messageKey = getTypeOptionMessageKey(type);

			return {
				products: typeMapping[type].products,
				value: type,
				trackingKey: type,
				queryParamValue: type,
				isDefault: false,
				label: config.intl?.formatMessage(typeLabels[messageKey]) || '',
				product: config.product,
			};
		});

	return [allOption, ...typeOptions];
};
